import React, { memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { TenderSearch } from '@app/common';
import { Tender, TenderCategory, UserType } from '@app/models';
import { useUserContext } from '@app/providers';
import { useNavigate } from 'react-router-dom';
import { Button } from '@app/components';
import { IconPlusCircle24 } from '@app/icons';
import { ModalTenderCreate } from '@app/modals';

interface Props {
  tenderCategory: TenderCategory;
  title?: string;
}

function PageProfileTenderSearch(props: Props) {
  const { tenderCategory, title } = props;
  const { user, company } = useUserContext();
  const navigate = useNavigate();
  const [modalTenderCreateVisible, setModalTenderCreateVisible] =
    useState<boolean>(false);

  const onClickTender = useCallback(
    (id: string) => () => {
      navigate(`/profile/tender/${id}`);
    },
    [navigate]
  );

  const onClickCreate = useCallback(() => {
    setModalTenderCreateVisible(true);
  }, []);

  const closeModalTenderCreate = useCallback(() => {
    setModalTenderCreateVisible(false);
  }, []);

  const onClickReplies = useCallback(() => {
    navigate(`/profile/tender/${tenderCategory}/replies`);
  }, [navigate, tenderCategory]);

  const controls = useMemo<ReactNode[]>(
    () => [
      <div
        key="controls"
        className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto"
      >
        <Button
          key="replies"
          text="Перейти в список моих откликов"
          onClick={onClickReplies}
          className="flex-1 min-w-0 sm:min-w-[200px]"
        />
        <Button
          key="create"
          text="Создать объявление"
          icon={IconPlusCircle24}
          onClick={onClickCreate}
          className="flex-1 min-w-0 sm:min-w-[200px]"
        />
      </div>,
    ],
    [onClickCreate, onClickReplies]
  );

  const defaultParams = useMemo<Partial<Tender>>(() => {
    const paramsToReturn: Partial<Tender> = {
      category: tenderCategory,
    };

    if (user.type === UserType.ENTITY) {
      paramsToReturn.companyId = company!.id;
    } else {
      paramsToReturn.ownerId = user.id;
    }

    return paramsToReturn;
  }, [tenderCategory, user, company]);

  return (
    <>
      <TenderSearch
        params={defaultParams}
        onClick={onClickTender}
        tenderCategory={tenderCategory}
        columns={4}
        title={title}
        controls={controls}
        emptyTitle={`Вы еще не опубликовали заявки в категории «${title}»`}
      />
      <ModalTenderCreate
        companyId={!!company ? company.id : null}
        visible={modalTenderCreateVisible}
        closeModal={closeModalTenderCreate}
        tenderCategory={tenderCategory}
      />
    </>
  );
}

export default memo(PageProfileTenderSearch);
